import { MessendzheryKlientki } from '../obrashcheniya/components/MessendzheryKlientki';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconChevronDown, IconGridDots, IconMicrophone, IconMicrophoneOff, IconPhone, IconPhoneOff, IconX } from '@tabler/icons-react';
import { usePermissions } from '../auth/usePermissions';
import { MaskaTelefona } from '../hodba/MaskaTelefona';
import { LiveRibbon } from './LiveRibbon';
import { CallCard } from './CallCard';
import { ConfirmHumanPanel } from './AfterCallPanel';
import { ManualDial } from './ManualDial';
import { subscribeManualDial } from './openManualDial';
import { ObrabotkaKnopki } from './ObrabotkaKnopki';
import { OutcomeChips } from './OutcomeChips';
import { fetchHistory, fetchOutcomes, setCallOutcome, type OutcomeChip } from './callApi';
import { formatDuration, statusLook } from './statusVocabulary';
import { maskPartialLocal } from './format';
import { usePhone } from './usePhone';
import { otmenitNabor, perezvonitPropushchennomu, vybrannayaLiniya, zapomnitLiniyu, type ActiveCall } from './workplaceApi';
import { useWorkplaceState } from './useWorkplaceState';
import { publishPhoneAlerts } from './phoneAlerts';
import { trevogiTelefona } from './otkazLinii';
import './phone-pult.css';

import { naborDlyaPerevoda, razobratNomer, type Nomer } from './nomerNabora';
import { useManualEntryLease } from './useManualEntryLease';
function subscribeSlot(update: () => void) {
  const observer = new MutationObserver(update);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}
const readSlot = () => document.getElementById('phone-pult-slot');
type Tab = 'card' | 'history' | 'dial';

/**
 * Lives beside the PhoneProvider above Routes; the header slot can change without losing call/results.
 *
 * header1 (макет Юрия 04.10.2026, «высотой в одну строку»): в строке шапки — статус сотрудника,
 * блок телефона и «Звонилка ▾». Линия, обзвон, очередь, ручной набор и карточка звонка — в
 * раскрываемом пульте поверх рабочего поля; высота шапки от звонка не растёт.
 */
export function PhonePult() {
  const slot = useSyncExternalStore(subscribeSlot, readSlot, () => null);
  const { can } = usePermissions();
  const allowed = can('tel.workplace');
  const workplace = useWorkplaceState(allowed);
  const { phone, connecting, connectError, connect, activeNumber, dialRevision = 0, vDrugomOkne = false } = usePhone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const state = workplace.state;
  const activeCall = state?.active_call ?? null;
  const inCall = ['dialing', 'early', 'talking'].includes(phone.callState) || state?.status === 'on_call';
  const processing = state?.status === 'processing';
  const [observedCall, setObservedCall] = useState<ActiveCall | null>(null);
  const [observedDial, setObservedDial] = useState(dialRevision);
  if (observedDial !== dialRevision) { setObservedDial(dialRevision); setObservedCall(null); }
  if (activeCall !== null && observedCall?.call_id !== activeCall.call_id) setObservedCall(activeCall);
  // `s726`: после перезагрузки страницы в «Обработке» экран разговора не помнит — его называет сервер.
  const currentCall = activeCall ?? (processing ? (observedCall ?? state?.posledniy_zvonok ?? null) : null);
  const [now, setNow] = useState(() => Date.now());
  /*
   * ⛔ `s726` «ТЕЛЕФОН ПОДКЛЮЧЁН» — ЭТО ТЕЛЕФОН ЭТОГО БРАУЗЕРА (находка `s724`). Раньше сюда
   * подмешивался ответ сервера «линия на связи», а он живёт ещё минуту после того, как страницу
   * перезагрузили: телефона уже нет, кнопки подключения тоже нет, человек числится «Готов».
   * Истина здесь одна — регистрация в этом окне либо в соседнем окне этого же браузера.
   */
  const connected = phone.registered || vDrugomOkne;
  const nabor = state?.nabor ?? null;
  /*
   * ⛔ `s731` (авария 08.10.2026: менеджер звонит на один номер, а пульт «высвечивает» прежний и потом
   * «перескакивает»). Звонок из карточки начинает станция: сначала она звонит в браузер менеджера, и
   * пока клиентка не ответила, разговора на сервере ещё нет. Пульт в это время брал номер, который этот
   * браузер набирал САМ в прошлый раз (`activeNumber` не стирается), — то есть прошлую клиентку.
   * Кого набирает станция, сервер говорит сразу (`nabor`); запоминаем его до появления разговора.
   */
  const [vidennyyNabor, setVidennyyNabor] = useState<typeof nabor>(null);
  if (nabor !== null && vidennyyNabor?.attempt_id !== nabor.attempt_id) setVidennyyNabor(nabor);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keys, setKeys] = useState('');
  const [tab, setTab] = useState<Tab>('card');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Nomer>({ pole: '+7', vid: 'nepolnyy', nabor: null, zametka: null });
  const entry = useManualEntryLease(open && tab === 'dial' && allowed && can('tel.manual_dial') && state?.workplace != null && !inCall && !processing && nabor === null);
  const [manualEntry, setManualEntry] = useState<{ number: string; revision: number } | null>(null);
  useEffect(() => {
    if (!open || manualEntry === null) return;
    const header = slot?.closest('.ops-global-header');
    header?.classList.add('phone-pult-from-card');

    return () => header?.classList.remove('phone-pult-from-card');
  }, [open, manualEntry, slot]);
  const [statusMenu, setStatusMenu] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [selectedCall, setSelectedCall] = useState<number | null>(null);
  useEffect(() => {
    if (slot === null || !allowed || !can('tel.manual_dial') || state?.workplace == null) return;

    return subscribeManualDial(number => {
      const parsed = razobratNomer(number, true);
      if (parsed.ok) setDraft(parsed.nomer);
      setManualEntry(previous => ({ number, revision: (previous?.revision ?? 0) + 1 }));
      setSelectedCall(null);
      setTab('dial');
      setOpen(true);
    });
  }, [slot, allowed, can, state?.workplace]);
  const [picked, setPicked] = useState<OutcomeChip | null>(null);
  const [recallAt, setRecallAt] = useState('');
  const [recallNote, setRecallNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [resultError, setResultError] = useState<string | null>(null);
  // s726: линия ручного набора — выбор человека, помнится в браузере.
  const [liniyaId, setLiniyaId] = useState<number | null>(() => vybrannayaLiniya());
  const callIdentity = useRef<number | string | null>(null);
  const currentIdentity = currentCall?.call_id ?? (activeNumber ? `dial:${dialRevision}` : null);
  const words = state?.words?.texts ?? {};
  const word = (name: string) => words[`tel.pult.${name}`] ?? `tel.pult.${name}`;
  const mayHistory = allowed && can('tel.journal.view');
  const historyKey = currentCall ? { related_call_id: currentCall.call_id } : activeNumber ? { phone: activeNumber } : null;
  const { data: history = [] } = useQuery({
    queryKey: ['telephony', 'history', historyKey], queryFn: () => fetchHistory(historyKey ?? {}),
    enabled: mayHistory && historyKey !== null && (open || processing), refetchInterval: processing ? 3000 : false,
  });
  const { data: outcomes = [] } = useQuery({ queryKey: ['telephony', 'outcomes'], queryFn: fetchOutcomes, enabled: mayHistory && (inCall || processing) });
  // Never promote an older related history row to the identity of the current call.
  const callId = currentCall?.call_id ?? null;
  const lastCall = callId !== null ? history.find(row => row.id === callId) ?? null : null;
  const localCallSeychas = ['dialing', 'early', 'talking'].includes(phone.callState);
  if (nabor === null && !localCallSeychas && vidennyyNabor !== null) setVidennyyNabor(null);
  // Набор станции (идущий либо только что соединённый) главнее номера, набранного этим браузером раньше.
  const naborStancii = nabor ?? (localCallSeychas ? vidennyyNabor : null);
  const masked = currentCall?.client_phone ?? lastCall?.client_phone ?? naborStancii?.client_phone ?? (activeNumber ? maskPartialLocal(activeNumber) : null);

  useEffect(() => {
    const header = slot?.closest<HTMLElement>('.ops-global-header');
    if (!header) return;
    const observer = new ResizeObserver(() => {
      header.style.setProperty('--ops-global-header-height', `${header.getBoundingClientRect().height}px`);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, [slot]);
  // header1: каркас прячет второстепенное на телефоне, но блок идущего звонка обязан остаться на виду.
  const callShown = allowed && (inCall || processing);
  useEffect(() => {
    const header = slot?.closest<HTMLElement>('.ops-global-header');
    if (!header) return;
    header.dataset.phoneCall = callShown ? '1' : '';
    return () => { header.dataset.phoneCall = ''; };
  }, [slot, callShown]);
  // s726: открытая карточка лида — окно поверх экрана. Пока идёт набор, разговор или обработка,
  // шапка с кнопками звонка обязана остаться доступной: поднимаем её над окном, а окно сдвигаем под неё.
  const callBusy = allowed && (inCall || processing || nabor !== null);
  useEffect(() => {
    const header = slot?.closest<HTMLElement>('.ops-global-header');
    if (!header) return;
    const root = document.documentElement;
    header.dataset.phoneBusy = callBusy ? '1' : '';
    root.dataset.phoneBusy = callBusy ? '1' : '';
    if (callBusy) root.style.setProperty('--ops-phone-busy-top', `${Math.round(header.getBoundingClientRect().bottom)}px`);
    return () => { header.dataset.phoneBusy = ''; root.dataset.phoneBusy = ''; };
  }, [slot, callBusy]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (currentIdentity === null || currentIdentity === callIdentity.current) return;
    callIdentity.current = currentIdentity;
    setPicked(null); setRecallAt(''); setRecallNote(''); setSelectedCall(null); setResultError(null); setKeypadOpen(false); setKeys('');
  }, [currentIdentity]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); setManualEntry(null); setDraft({ pole: '+7', vid: 'nepolnyy', nabor: null, zametka: null }); setStatusMenu(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const saveResult = useCallback(async (outcome: OutcomeChip, recall?: { at: string; note?: string; assign_to_me?: boolean }) => {
    if (callId === null || saving) return;
    setSaving(true); setResultError(null);
    try {
      await setCallOutcome(callId, { outcome_id: outcome.id, ...(recall ? { recall } : {}) });
      setPicked(outcome);
      await queryClient.invalidateQueries({ queryKey: ['telephony', 'history'] });
    } catch (error: unknown) {
      setResultError((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? (state?.words?.texts['tel.pult.save_failed'] ?? 'tel.pult.save_failed'));
    } finally { setSaving(false); }
  }, [callId, saving, queryClient, state?.words]);

  // `s730`: перезвон по пропущенному входящему — звонок начинает станция, отказ говорит словами сервера.
  const [perezvon, setPerezvon] = useState<number | null>(null);
  const perezvonit = useCallback(async (taskId: number) => {
    if (perezvon !== null) return;
    setPerezvon(taskId); setResultError(null);
    try {
      await perezvonitPropushchennomu(taskId);
      setTab('card');
    } catch (error) {
      setResultError((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? (state?.words?.texts['tel.pult.missed_failed'] ?? 'tel.pult.missed_failed'));
    } finally { setPerezvon(null); }
  }, [perezvon, state?.words]);

  const [trayLeft, setTrayLeft] = useState<number | null>(null);
  const dialerRef = useRef<HTMLButtonElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const errorList = [connectError, phone.error, workplace.error, resultError].filter((e): e is string => Boolean(e));
  // `s729`: отказ линии («занято») виден в пульте, но в колокол шапки не идёт — это исход набора.
  const otkaz = phone.otkaz ?? null;
  const errorKey = trevogiTelefona(errorList, otkaz).join('\n');
  const mayReconnect = allowed && !connected && !['dialing', 'early', 'talking'].includes(phone.callState) && !connecting;
  const connectRef = useRef(connect);
  useEffect(() => { connectRef.current = connect; }, [connect]);
  const reconnect = useCallback(() => void connectRef.current(), []);
  useEffect(() => {
    publishPhoneAlerts({ errors: errorKey === '' ? [] : errorKey.split('\n'), reconnect: mayReconnect ? reconnect : null });
  }, [errorKey, mayReconnect, reconnect]);
  useEffect(() => () => publishPhoneAlerts({ errors: [], reconnect: null }), []);
  useEffect(() => {
    if (!statusMenu) return;
    const onDown = (event: MouseEvent) => { if (!statusRef.current?.contains(event.target as Node)) setStatusMenu(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [statusMenu]);
  // Пульт раскрывается под «Звонилкой», но не выходит за правый край шапки.
  const placeTray = useCallback(() => {
    const button = dialerRef.current;
    const header = button?.closest<HTMLElement>('.ops-global-header');
    if (!button || !header) return;
    const box = header.getBoundingClientRect();
    const width = Math.min(540, box.width - 32);
    setTrayLeft(Math.max(16, Math.min(button.getBoundingClientRect().left - box.left, box.width - width - 16)));
  }, []);
  useEffect(() => {
    if (!open) return;
    placeTray();
    window.addEventListener('resize', placeTray);
    return () => window.removeEventListener('resize', placeTray);
  }, [open, placeTray]);

  if (slot === null || !allowed || state?.workplace == null) return null;
  const actualLook = statusLook(state.status);
  // Подключить телефон можно в любом статусе, кроме самого разговора: и в «Обработке» тоже.
  const linii = state.linii ?? [];
  const propushchennye = state.propushchennye ?? [];
  // `s730`: перевод рисуется, только когда станция умеет его исполнить (живая проверка: перевод обрывал разговор).
  const mayTransfer = can('tel.transfer') && state.perevod === true;
  // Выбранной линии может уже не быть среди действующих — тогда звонит линия рабочего места,
  // а нет и её — первая из списка: ровно та, что стоит в селекторе «Звоним с линии».
  const liniyaNabora = liniyaId !== null && linii.some(l => l.id === liniyaId) ? liniyaId : (state.workplace?.line?.id ?? linii[0]?.id ?? null);
  const localCall = ['dialing', 'early', 'talking'].includes(phone.callState);
  const offline = !connected && !localCall;
  const unavailableReady = offline && state.status === 'ready';
  const openCard = (url: string) => { setOpen(false); navigate(url); };
  const attemptText = (popytka?: number | null, vsego?: number | null) => (popytka
    ? `${popytka}${vsego ? ` ${word('attempt_of')} ${vsego}` : ''}` : word('no_data'));
  const label = unavailableReady ? word('unavailable') : actualLook.label;
  const canOperate = phone.registered;
  const look = unavailableReady ? { color: 'var(--ops-warning)', soft: 'var(--ops-warning-soft)' } : actualLook;
  // `s729`: вернулся к ручному набору или закрыл пульт — строка отказа прошлой попытки гаснет.
  const show = (next: Tab) => { if (next === 'dial') phone.sbrositOtkaz?.(); setTab(next); setOpen(true); setSelectedCall(null); };
  const zakryt = () => { phone.sbrositOtkaz?.(); setOpen(false); setManualEntry(null); setDraft({ pole: '+7', vid: 'nepolnyy', nabor: null, zametka: null }); };
  // Имени нет — показываем номер краями, а не «номер неизвестен»: номер как раз известен.
  // `s729`: у ручного набора до ответа звонка на сервере ещё нет — номер знает этот браузер,
  // и пока идёт набор или висит его отказ, в заголовке он, а не «Номер неизвестен».
  const nabranZdes = naborStancii === null && activeNumber && (localCall || otkaz !== null) ? maskPartialLocal(activeNumber) : null;
  const clientName = (currentCall?.contact_name || null) ?? currentCall?.client_phone
    ?? (naborStancii !== null ? (naborStancii.contact_name || naborStancii.client_phone) : null) ?? (nabranZdes || null) ?? word('client_unknown');
  /*
   * s732: линия ИМЕННО текущего звонка. Набор станции и разговор называют её с сервера (её записал
   * учёт). Звонок, который этот браузер набрал сам, идёт только через линию рабочего места — пока
   * сервер о нём ещё не знает, показываем её. Сохранённый выбор прошлого звонка сюда не попадает.
   */
  const zvonokSeychas = inCall || nabor !== null;
  // Живая проба 08.10.2026: входящий браузер принимает сам, и он тоже «звонок в этом браузере» — пульт
  // показывал у него линию рабочего места. Линия места — только у номера, набранного здесь рукой.
  const svoyNabor = localCall && naborStancii === null && Boolean(activeNumber) && activeCall?.direction !== 'inbound';
  const liniyaZvonka = !zvonokSeychas ? null
    : (activeCall?.line ?? nabor?.line ?? (svoyNabor ? (state.workplace?.line?.name ?? null) : null));
  // У перезвона по пропущенному источник сохраняется и после ответа станции.
  const zvonokObzvona = currentCall ?? naborStancii;
  const proektObzvona = zvonokObzvona !== null && zvonokObzvona.task_id != null && (zvonokObzvona.ruchnoy !== true || zvonokObzvona.propushchennyy === true)
    ? (zvonokObzvona.source ?? null) : null;
  const timer = processing
    ? (lastCall?.talk_sec !== null && lastCall?.talk_sec !== undefined ? formatDuration(lastCall.talk_sec) : '—')
    : formatDuration(phone.talkStartedAt ? Math.max(0, Math.floor((now - phone.talkStartedAt) / 1000)) : workplace.secondsInStatus);

  return createPortal(
    <section className="ops-skin phone-pult" data-phone-pult>
      {/* 2. Статус сотрудника: цвет и подпись. Подключённый телефон не делает человека «Готов». */}
      <div className="hdr-item hdr-status" data-hdr="status" ref={statusRef}>
        <button className="hdr-btn" onClick={() => setStatusMenu(!statusMenu)} aria-expanded={statusMenu}>
          <span className="phone-pult-dot" style={{ background: look.color }} /><span className="hdr-status-label">{label}</span><IconChevronDown size={16} className="hdr-chevron" />
        </button>
        {statusMenu && <div className="phone-pult-status-menu">
          <button disabled={!connected || workplace.busy || state.lock.locked} onClick={() => { setStatusMenu(false); void workplace.setReady(); }}>{word('ready')}</button>
          {state.break_presets_minutes.map(minutes => <button key={minutes} disabled={workplace.busy || state.lock.locked} onClick={() => { setStatusMenu(false); void workplace.setBreak(minutes); }}>{word('break')} · {formatDuration(minutes * 60)}</button>)}
          {state.away_kinds.length === 0 && <button disabled={workplace.busy || state.lock.locked} onClick={() => { setStatusMenu(false); void workplace.setAway(); }}>{word('away')}</button>}
          {state.away_kinds.map(kind => <button key={kind.id} disabled={workplace.busy || state.lock.locked} onClick={() => { setStatusMenu(false); void workplace.setAway(kind.id); }}>{word('away')} · {kind.name}</button>)}
        </div>}
      </div>

      {/* 3. Подключение телефона — отдельное состояние; во время звонка здесь клиентка, таймер, микрофон, завершение. */}
      {offline && <button className="hdr-item hdr-btn hdr-primary" data-hdr="connect" disabled={connecting} onClick={() => void connect()} title={errorList[0] ?? undefined}>
        <IconPhone size={20} /><span className="hdr-long">{connecting ? word('connecting') : word('connect_phone')}</span><span className="hdr-short">{connecting ? word('connecting') : word('connect')}</span>
      </button>}
      {!offline && !inCall && !processing && nabor === null && <button className="hdr-item hdr-btn" data-hdr="phone-on" onClick={() => show('dial')}>
        <IconPhone size={20} className="hdr-phone-on" /><span className="hdr-long">{word(phone.registered ? 'phone_on' : 'other_window')}</span>
      </button>}
      {/* `s726`: идёт набор — кого и зачем, ещё до соединения; отмена кладёт гудки клиентке. */}
      {nabor !== null && !inCall && !processing && <div className="hdr-item hdr-call" data-hdr="nabor">
        <span className="hdr-nabor-label">{word('dialing')}</span>
        <button className="hdr-call-client" onClick={() => show('card')} title={nabor.source ?? undefined}>{nabor.contact_name || nabor.client_phone}</button>
        <button className="hdr-btn hdr-icon hdr-danger" onClick={() => void otmenitNabor().catch(() => undefined)} aria-label={word('cancel_dial')} title={word('cancel_dial')}><IconPhoneOff size={20} /></button>
      </div>}
      {(inCall || processing) && <div className="hdr-item hdr-call" data-hdr="call">
        <button className="hdr-call-client" onClick={() => show('card')} title={clientName}>{clientName}</button>
        {callId !== null && <span className="hdr-call-number"><MaskaTelefona masked={masked} entityType="tel_calls" entityId={callId} bezZvonka /></span>}
        <strong className="hdr-call-timer">{timer}</strong>
        {inCall && <>
          <button className="hdr-btn hdr-icon" disabled={!canOperate} onClick={phone.toggleMute} aria-label={word(phone.muted ? 'mic_off' : 'mic')} title={word(phone.muted ? 'mic_off' : 'mic')}>{phone.muted ? <IconMicrophoneOff size={20} /> : <IconMicrophone size={20} />}</button>
          <button className="hdr-btn hdr-icon hdr-danger" disabled={!canOperate} onClick={() => void phone.hangup()} aria-label={word('end')} title={word('end')}><IconPhoneOff size={20} /></button>
        </>}
        {processing && <>
          <button className="hdr-btn" onClick={() => show('card')}>{word('result')}</button>
          {/* `s779`: отсчёт срока обработки — на самой кнопке «Завершить», рядом «+» — продлить на 1–5 минут. */}
          <ObrabotkaKnopki workplace={workplace} word={word} />
        </>}
      </div>}

      {/* `s730`: клиент ждёт на линии, пока человек занят или дооформляет звонок, — видно сразу, без пульта. */}
      {(state.zhdut_na_linii ?? 0) > 0 && <span className="hdr-item hdr-waiting" data-hdr="waiting" role="status">{word('waiting_on_line')}: {state.zhdut_na_linii}</span>}

      {/* 4. «Звонилка ▾» раскрывает существующий пульт. */}
      <button ref={dialerRef} className="hdr-item hdr-btn hdr-dialer" data-hdr="dialer" aria-expanded={open} aria-label={word('dialer')} title={word('dialer')}
        onClick={() => { if (open) zakryt(); else show(inCall || processing ? 'card' : 'dial'); }}>
        <IconGridDots size={20} /><span className="hdr-label">{word('dialer')}</span>
        {propushchennye.length > 0 && <span className="hdr-missed" data-hdr="missed" title={word('missed_title')} aria-label={`${word('missed_title')}: ${propushchennye.length}`}>{propushchennye.length}</span>}
        <IconChevronDown size={16} className="hdr-chevron" />
      </button>

      {open && <div className="phone-pult-tray" role="region" aria-label={word('dialer')} style={trayLeft === null ? undefined : { left: trayLeft }}>
        <div className="phone-pult-tray-title"><strong>{processing && tab === 'card' ? word('result') : tab === 'dial' ? word('manual') : clientName}</strong><button onClick={zakryt} aria-label={word('close')}><IconX size={20} /></button></div>
        {/*
          s732 (владелец 08.10.2026). «Менеджер» убран: человеку незачем читать, кто он сам. Линия
          рабочего места сверху убрана: до звонка линию выбирает один селектор «Звоним с линии», а
          здесь она появляется только у идущего набора или разговора — фактическая, этого звонка.
          «Обзвон» виден только у звонка, который начал обзвон. Счётчики названы по смыслу и не
          смешаны: люди, которые сейчас слушают очередь, и задачи перезвона, которым пора.
        */}
        <dl className="phone-pult-facts">
          {zvonokSeychas && <div data-pult="call-line"><dt>{word('call_line')}</dt><dd>{liniyaZvonka ?? word('no_data')}</dd></div>}
          {proektObzvona !== null && <div data-pult="project"><dt>{word('dialer_project')}</dt><dd>{proektObzvona}</dd></div>}
          <div data-pult="on-line"><dt>{word('waiting_on_line')}</dt><dd>{state.zhdut_na_linii ?? 0}</dd></div>
          <div data-pult="recall-queue"><dt>{word('queue_recall')}</dt><dd>{state.perezvonov ?? state.ochered ?? 0}</dd></div>
          {(state.perezvonov_pozzhe ?? 0) > 0 && <div data-pult="recall-later"><dt>{word('queue_recall_later')}</dt><dd>{state.perezvonov_pozzhe}</dd></div>}
          {(state.v_obzvonah ?? 0) > 0 && <div data-pult="dialer-queue"><dt>{word('queue_dialer')}</dt><dd>{state.v_obzvonah}</dd></div>}
          {(currentCall ?? nabor) !== null && <div><dt>{word('attempt')}</dt><dd>{attemptText((currentCall ?? nabor)?.popytka, (currentCall ?? nabor)?.popytok_vsego)}</dd></div>}
          {(currentCall ?? nabor)?.kartochka != null && <div><dt>{word('card')}</dt><dd><button className="phone-pult-link" onClick={() => openCard((currentCall ?? nabor)!.kartochka!.url)}>{word('open_card')}</button></dd></div>}
          {(currentCall ?? nabor)?.kartochka != null && <div className="phone-pult-fact-wide"><MessendzheryKlientki card={(currentCall ?? nabor)!.kartochka!} /></div>}
          {(currentCall ?? nabor)?.kommentariy ? <div className="phone-pult-fact-wide"><dt>{word('comment')}</dt><dd>{(currentCall ?? nabor)!.kommentariy}</dd></div> : null}
        </dl>
        {errorList.length > 0 && <div className="phone-pult-error" role="alert">{errorList[0]}{mayReconnect && <button onClick={() => void connect()}>{word('reconnect')}</button>}</div>}
        {propushchennye.length > 0 && <div className="phone-pult-missed" data-pult="missed">
          <strong>{word('missed_title')}</strong>
          {propushchennye.map(p => <div className="phone-pult-missed-row" key={p.task_id}>
            <span>{p.at ? new Date(p.at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
            <span>{p.client_phone ?? word('client_unknown')}</span>
            <span className="phone-pult-secondary">{p.liniya_nedostupna ? `${p.line ?? ''} · ${word('line_down')}` : (p.line ?? '')}</span>
            <button disabled={!phone.registered || inCall || processing || nabor !== null || perezvon !== null || !can('tel.manual_dial')} onClick={() => void perezvonit(p.task_id)}>{word('missed_call_back')}</button>
            {(p.obzvony?.length ?? 0) > 0 && <span className="phone-pult-missed-robot" data-pult="missed-project">{word('dialer_project')}: {p.obzvony!.map(o => o.name).join(' · ')}</span>}
            {/* s777 (обращение №11): робот уже набирал, а строка молчала — менеджер видела бездействие. */}
            {p.robot ? <span className="phone-pult-missed-robot" data-pult="missed-robot">{p.robot}</span> : null}
          </div>)}
        </div>}
        <nav className="phone-pult-tabs">{(['card', 'history', 'dial'] as Tab[]).map(item => <button key={item} className={tab === item ? 'active' : ''} onClick={() => show(item)}>{word(item === 'dial' ? 'manual' : item)}</button>)}</nav>
        {tab === 'dial' && <><ManualDial draft={draft} onDraft={setDraft} editingDisabled={!entry.ready} line={state.workplace.line} busy={!entry.ready || !phone.registered || inCall || processing} canDial={can('tel.manual_dial')} linii={linii} vybrana={liniyaNabora} onVybor={id => { setLiniyaId(id); zapomnitLiniyu(id); }} onCall={number => { if (liniyaNabora !== null) void entry.call(number, liniyaNabora).then(started => { if (started) setTab('card'); }); }} />{entry.error && <div className="phone-pult-error" role="alert">{words[entry.error] ?? entry.error}</div>}{!connected && <div className="phone-pult-secondary">{word('phone_only')}</div>}</>}
        {tab === 'history' && <div>{history.length === 0 ? <p className="phone-pult-secondary">{word('history_empty')}</p> : history.map(call => <button className="phone-pult-history-row" key={call.id} onClick={() => setSelectedCall(call.id)}><span>{call.at ? new Date(call.at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span><span>{formatDuration(call.talk_sec ?? 0)}</span></button>)}{selectedCall !== null && mayHistory && <CallCard callId={selectedCall} onClose={() => setSelectedCall(null)} />}</div>}
        {tab === 'card' && <>
          {inCall && <>
            <LiveRibbon transcript={workplace.transcript} operatorName={state.workplace.display_name} clientPhone={masked ?? ''} />
            {canOperate && <div className="phone-pult-advanced">
              <button onClick={() => void phone.toggleHold()}>{word(phone.onHold ? 'resume' : 'hold')}</button>
              {mayTransfer && <button onClick={() => setTransferOpen(!transferOpen)}>{word('transfer')}</button>}
              <button aria-expanded={keypadOpen} onClick={() => setKeypadOpen(!keypadOpen)}>{word('keypad')}</button>
              {keypadOpen && <div className="phone-pult-keypad" role="group" aria-label={word('keypad')}>
                <output aria-live="polite" title={word('keypad_hint')}>{keys || '\u00A0'}</output>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(key => <button type="button" key={key} onClick={() => { if (phone.sendDtmf(key)) setKeys(prev => (prev + key).slice(-24)); }}>{key}</button>)}
              </div>}
              {transferOpen && mayTransfer && <form onSubmit={event => { event.preventDefault(); const nabor = naborDlyaPerevoda(transferNumber); if (nabor !== null) { void phone.transferTo(nabor); setTransferOpen(false); } }}>
                <input aria-label={word('transfer_number')} placeholder={word('transfer_number')} value={transferNumber} onChange={event => setTransferNumber(event.target.value)} />
                <button disabled={naborDlyaPerevoda(transferNumber) === null}>{word('transfer_now')}</button>
              </form>}
            </div>}
          </>}
          {callId !== null ? <>
            {(processing || inCall) && <div>
              {processing && lastCall?.outcome.needs_confirm && <ConfirmHumanPanel onAnswer={async answer => { await setCallOutcome(callId, { confirm_manager: answer }); await queryClient.invalidateQueries({ queryKey: ['telephony', 'history'] }); }} />}
              {mayHistory && <OutcomeChips outcomes={outcomes} pickedOutcomeId={picked?.id ?? lastCall?.outcome.business_outcome_id ?? null} onPick={outcome => { if (saving) return; if (outcome.action === 'recall_later') setPicked(outcome); else void saveResult(outcome); }} />}
              {mayHistory && picked?.action === 'recall_later' && <form className="phone-pult-recall" onSubmit={event => { event.preventDefault(); if (recallAt) void saveResult(picked, { at: new Date(recallAt).toISOString(), note: recallNote || undefined, assign_to_me: false }); }}>
                <label>{word('recall_when')}<input type="datetime-local" required value={recallAt} onChange={event => setRecallAt(event.target.value)} /></label>
                <label>{word('recall_note')}<textarea rows={2} value={recallNote} onChange={event => setRecallNote(event.target.value)} /></label>
                <button className="phone-pult-primary" disabled={saving}>{word(saving ? 'saving' : 'save')}</button>
              </form>}
            </div>}
            {mayHistory && <CallCard callId={callId} onClose={() => setOpen(false)} />}
          </> : nabor !== null
            ? <p className="phone-pult-secondary">{word('dialing')}: {nabor.contact_name || nabor.client_phone}{nabor.source ? ` · ${nabor.source}` : ''}</p>
            : <p className="phone-pult-secondary">{word('card_empty')}</p>}
        </>}
      </div>}
    </section>, slot,
  );
}
